import { useCallback, useEffect, useMemo, useReducer } from "react";
import imageCompression from "browser-image-compression";
import type { UploadConfig } from "../../domain/entities/UploadConfig.js";
import type { UploadItem, UploadStatus } from "../../domain/entities/UploadItem.js";
import { container } from "../../../../shared/di/container.js";

// crypto.randomUUID needs a secure context (https or localhost) — unavailable
// when the dashboard is opened over plain http via a LAN IP.
function uuid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: "ADD"; item: UploadItem }
  | { type: "UPDATE"; id: string; patch: Partial<UploadItem> }
  | { type: "REMOVE"; id: string }
  | { type: "SET_ALL"; items: UploadItem[] };

function reducer(state: UploadItem[], action: Action): UploadItem[] {
  switch (action.type) {
    case "ADD":
      return [...state, action.item];
    case "UPDATE":
      return state.map((it) => (it.id === action.id ? { ...it, ...action.patch } : it));
    case "REMOVE":
      return state.filter((it) => it.id !== action.id);
    case "SET_ALL":
      return action.items;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { UploadPath } from "../../presentation/paths/UploadPath.js";

function buildRemotePath(config: UploadConfig, file: File): string {
  return UploadPath.buildFilePath(config.remoteFolder, file.name);
}

// Recover the real object path (inside the bucket) from a stored Supabase
// storage URL, so deletes target the object rather than the full URL.
// Signed:  <base>/storage/v1/object/sign/<bucket>/<path>?token=...
// Public:  <base>/storage/v1/object/public/<bucket>/<path>
// Falls back to the input unchanged if it isn't a parseable URL (e.g. already
// a bare path) or the bucket segment isn't found.
function objectPathFromUrl(url: string, bucket: string): string {
  try {
    const { pathname } = new URL(url);
    const marker = `/${bucket}/`;
    const idx = pathname.indexOf(marker);
    if (idx === -1) return url;
    return decodeURIComponent(pathname.slice(idx + marker.length));
  } catch {
    return url;
  }
}

async function applyCompression(file: File, quality: number): Promise<File> {
  try {
    return await imageCompression(file, {
      maxSizeMB: 5,
      maxWidthOrHeight: 1920,
      initialQuality: quality,
      useWebWorker: true,
    });
  } catch (err) {
    if (import.meta.env.DEV) console.warn("applyCompression failed, using original:", err);
    return file;
  }
}

async function applyWatermark(
  file: File,
  serverTime: Date,
  stampLines: string[],
): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;

      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(objectUrl);

      const dateStr = serverTime.toLocaleString("sv-SE").replace("T", " ").slice(0, 16);
      const lines = [dateStr, ...stampLines];
      const fontSize = Math.max(14, Math.floor(canvas.width / 40));
      ctx.font = `bold ${fontSize}px sans-serif`;

      const padding = fontSize * 0.6;
      const lineHeight = fontSize * 1.4;
      const boxH = lines.length * lineHeight + padding * 2;
      const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + padding * 2;
      const x = padding;
      const y = canvas.height - boxH - padding;

      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fillRect(x, y, boxW, boxH);

      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.8)";
      ctx.shadowBlur = 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, x + padding, y + padding + fontSize + i * lineHeight);
      });

      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.9,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUploadController(config: UploadConfig) {
  const [items, dispatch] = useReducer(reducer, []);

  const uploadedUrls = useMemo(
    () => items.filter((i) => i.status === "done" && i.publicUrl).map((i) => i.publicUrl!),
    [items],
  );

  // ── Offline flush ─────────────────────────────────────────────────────────

  const flushOfflineQueue = useCallback(async () => {
    if (!navigator.onLine) return;
    const entries = await container.upload.loadOfflineQueueUseCase();
    for (const entry of entries) {
      const file = new File([entry.fileBlob], entry.fileName, { type: entry.mimeType });
      const item: UploadItem = {
        id: entry.id,
        file,
        objectUrl: null,
        bucket: entry.bucket,
        remotePath: entry.remotePath,
        status: "uploading" as UploadStatus,
        progress: 0,
        publicUrl: null,
        errorMessage: null,
        createdAt: entry.createdAt,
        retryCount: entry.retryCount,
        isOfflineQueued: true,
      };
      dispatch({ type: "ADD", item });
      try {
        const url = await container.upload.uploadFileUseCase(item, (p) => {
          dispatch({ type: "UPDATE", id: item.id, patch: { progress: p } });
        });
        dispatch({
          type: "UPDATE",
          id: item.id,
          patch: { status: "done", progress: 1, publicUrl: url, isOfflineQueued: false },
        });
        await container.upload.removeFromQueueUseCase(item.id);
      } catch (e: any) {
        dispatch({
          type: "UPDATE",
          id: item.id,
          patch: { status: "failed", errorMessage: e?.message ?? "Upload failed" },
        });
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("online", flushOfflineQueue);
    flushOfflineQueue();
    return () => window.removeEventListener("online", flushOfflineQueue);
  }, [flushOfflineQueue]);

  // ── Core upload ───────────────────────────────────────────────────────────

  const _doUpload = useCallback(async (item: UploadItem, processedFile: File) => {
    const uploadItem = { ...item, file: processedFile };
    dispatch({ type: "UPDATE", id: item.id, patch: { status: "uploading", file: processedFile } });
    try {
      const url = await container.upload.uploadFileUseCase(uploadItem, (p) => {
        dispatch({ type: "UPDATE", id: item.id, patch: { progress: p } });
      });
      dispatch({
        type: "UPDATE",
        id: item.id,
        patch: { status: "done", progress: 1, publicUrl: url },
      });
    } catch (e: any) {
      if (!navigator.onLine) {
        const entry = {
          id: item.id,
          fileBlob: processedFile,
          fileName: processedFile.name,
          mimeType: processedFile.type || "application/octet-stream",
          bucket: item.bucket,
          remotePath: item.remotePath,
          retryCount: item.retryCount,
          createdAt: item.createdAt,
        };
        await container.upload.enqueueOfflineUseCase(entry);
        dispatch({
          type: "UPDATE",
          id: item.id,
          patch: { status: "offlineQueued", isOfflineQueued: true },
        });
      } else {
        dispatch({
          type: "UPDATE",
          id: item.id,
          patch: { status: "failed", errorMessage: e?.message ?? "Upload failed" },
        });
      }
    }
  }, []);

  // ── Preprocessing pipeline ────────────────────────────────────────────────

  const _preprocess = useCallback(
    async (file: File, serverTime: Date): Promise<File> => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) return file;

      let f = file;
      if (config.enableCompression !== false) {
        f = await applyCompression(f, config.compressionQuality ?? 0.6);
      }
      if (config.enableWatermark) {
        f = await applyWatermark(f, serverTime, config.stampLines ?? []);
      }
      return f;
    },
    [config],
  );

  // ── Public API ────────────────────────────────────────────────────────────

  const pickAndUpload = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const maxFiles = config.maxFiles ?? 0;
      const remaining = maxFiles > 0 ? maxFiles - items.length : fileArray.length;
      const toProcess = fileArray.slice(0, remaining);
      if (!toProcess.length) return;

      const serverTime = config.enableWatermark
        ? await container.upload.fetchServerTimeUseCase()
        : new Date();

      for (const file of toProcess) {
        const objectUrl = URL.createObjectURL(file);
        const remotePath = buildRemotePath(config, file);
        const item: UploadItem = {
          id: uuid(),
          file,
          objectUrl,
          bucket: config.bucket,
          remotePath,
          status: "pending",
          progress: 0,
          publicUrl: null,
          errorMessage: null,
          createdAt: new Date(),
          retryCount: 0,
          isOfflineQueued: false,
        };
        dispatch({ type: "ADD", item });
        const processed = await _preprocess(file, serverTime);
        await _doUpload(item, processed);
      }
    },
    [items.length, config, _preprocess, _doUpload],
  );

  const setFromUrls = useCallback(
    (urls: string[]) => {
      const syntheticItems: UploadItem[] = urls.map((url) => ({
        id: uuid(),
        file: new File([], url.split("/").pop() ?? "image"),
        objectUrl: null,
        bucket: config.bucket,
        // Real object path (not the full signed URL) so removeItem's storage
        // delete matches the object instead of silently no-op'ing → orphan.
        remotePath: objectPathFromUrl(url, config.bucket),
        status: "done" as UploadStatus,
        progress: 1,
        publicUrl: url,
        errorMessage: null,
        createdAt: new Date(),
        retryCount: 0,
        isOfflineQueued: false,
      }));
      dispatch({ type: "SET_ALL", items: syntheticItems });
    },
    [config.bucket],
  );

  const removeItem = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
      if (item.status === "done") {
        try {
          await container.upload.deleteFileUseCase(item.bucket, item.remotePath);
        } catch (err) {
          // Best-effort: storage deletion may fail on stale token / network /
          // already-deleted file. Log so orphans can be reaped server-side.
          if (import.meta.env.DEV) console.warn("Storage delete failed for", item.remotePath, err);
        }
      }
      dispatch({ type: "REMOVE", id });
    },
    [items],
  );

  const retry = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const updated: UploadItem = {
        ...item,
        status: "pending",
        progress: 0,
        errorMessage: null,
        retryCount: item.retryCount + 1,
      };
      dispatch({ type: "UPDATE", id, patch: updated });
      const serverTime = config.enableWatermark
        ? await container.upload.fetchServerTimeUseCase()
        : new Date();
      const processed = await _preprocess(item.file, serverTime);
      await _doUpload(updated, processed);
    },
    [items, config, _preprocess, _doUpload],
  );

  return { items, pickAndUpload, setFromUrls, removeItem, retry, uploadedUrls };
}
